import { View } from "react-native";
import { VDBText } from "@components/atoms";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import React, { memo, useCallback, useContext, useState } from "react";
import ImageTextListSection from "@components/atoms/Filters/ImageTextListSection";
import RangeListSection from "@components/atoms/Filters/RangeListSection";
import TabsSection from "@components/atoms/Filters/TabsSection";
import CheckBoxSection from "@components/atoms/CheckBoxSection";
import TextListSection from "@components/atoms/Filters/TextListSection";
import RangeSliderSection from "@components/atoms/RangeSliderSection";
import RangeSection from "@components/atoms/Filters/RangeSection";
import SearchFilterSection from "@components/atoms/Filters/SearchFilterSection";
import { DesignOptions } from "@components/atoms/Filters/types";
import { getStyleForSearchCategory } from "@components/atoms/Filters/utils";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import TextListSectionWithValues from "../TextListSectionWithValues";
import JewelryTypeComponent from "../JewelryTypeComponent";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ListSection from "@components/atoms/Filters/ListSection";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/native";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import ColumnSection from "@components/atoms/Filters/ColumnSection";
import { getBoxShadow } from "@utils/product/productValidators";
import { useSelector } from "react-redux";
import { ProductCategory } from "@utils/constants";
import ImageTextListRangeFilter from "../ImageTextListRangeFilter";
import SelectListItem from "@components/atoms/Filters/SelectListItem/SelectListItem";
import CollectionDropdown from "@components/atoms/Filters/CollectionDropdown";
import { useTypographyStore } from "@redux/slices/typographySlice";
import CollapsibleHeader from "@components/atoms/CollapsibleHeader";
import CollectionSubcategories from "@components/atoms/CollectionSubcategories";
import LabelOnlySection from "../LabelOnlySection";
import IFrameSection from "../IFrameSection";
import { normalizeBoolean } from "../ButtonSection/Utils";
import SearchStockSection from "../SearchStockSection";
import { useEffect } from "react";
import { Platform, DeviceEventEmitter } from "react-native";
import {
  useEnableProductSpecificFilterButtonColor,
  useHideFiltersBasedOnAllowedStockItemsType,
} from "@redux/slices/globalSlice";
import { DefaultTheme } from "styled-components/native";
import { css } from "styled-components/native";
import TextListSectionWithDynamicWidth from "../TextListSectionWithDynamicWidth";

type FilterSectionProps = {
  filterData: object;
  style?: any;
  sectionName?: string;
  selectedProduct?: string;
  filterContainerWidth?: number;
  fromUnifiedFilters?: boolean;
  scrollRef?: any;
  enterSecondFlow?: boolean;
};

const STABLE_EMPTY_PAYLOAD = {};
const STABLE_EMPTY_COLLECTIONS: string[] = [];

const isCollectionDepsMet = (
  deps: string[] | undefined,
  collections: string[] | null | undefined
) => {
  if (!deps || deps.length === 0) return true;
  if (!collections || collections.length === 0) return false;
  return deps.some((dep) =>
    collections.some(
      (collection) => collection === dep || collection.includes(dep)
    )
  );
};

const areCollectionsEqualForDeps =
  (deps: string[] | undefined) =>
  (prev: string[] | undefined, next: string[] | undefined) =>
    isCollectionDepsMet(deps, prev) === isCollectionDepsMet(deps, next);

// @ts-ignore
const renderItems = (
  data: any,
  section: string,
  fromResultScreen: boolean,
  selectedStyle: SelectedItemTheme,
  index: number,
  selectedProduct: ProductCategory,
  step: number,
  filterContainerWidth: number,
  theme: DefaultTheme,
  t: any,
  parentDirection: string,
  fromUnifiedFilters: boolean,
  scrollRef: any,
  enterSecondFlow = false,
  filtersPayload: any,
  selectedCollectionNames: string[],
  userInfo?: any,
  hideFiltersBasedOnAllowedStockItemsType?: boolean
) => {
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const type = data?.type;
  const options = data?.options;
  const fixedDirection =
    (theme.isLargeScreen || theme.isTablet) && !!fromResultScreen
      ? "column"
      : null;
  const filterItemKey =
    (Array.isArray(data?.payload_name)
      ? data?.payload_name?.[0]
      : data?.payload_name) ||
    data?.name ||
    data?.type ||
    index;
  const filterBaseId = `filters.${
    section?.toLowerCase()?.replaceAll(" ", "-")?.toString() ?? "section"
  }.${filterItemKey}.${type ?? "type"}`;
  const wrapFilter = (suffix: string, node: React.ReactNode) => {
    if (node === null || node === undefined || typeof node === "boolean") {
      return null;
    }

    if (typeof node === "object" && !React.isValidElement(node)) {
      console.warn("Invalid React child:", node);
      return null;
    }

    return (
      <View
        key={`${filterBaseId}.${suffix}`}
        testID={`${filterBaseId}.${suffix}`}
      >
        {node}
      </View>
    );
  };

  const checkDependencies = (
    dependencies: Array<{ key: string; value: any }>,
    parentSectionName: string,
    payload: any
  ) => {
    if (!dependencies || dependencies.length === 0) {
      return true;
    }
    if (!payload || !parentSectionName || !payload[parentSectionName]) {
      return false;
    }

    return dependencies.some((dep) => {
      const payloadValue = payload[parentSectionName]?.[dep.key];
      if (!payloadValue) {
        return false;
      }
      if (Array.isArray(payloadValue)) {
        return payloadValue.includes(dep.value);
      }
      return payloadValue === dep.value;
    });
  };

  const checkCollectionDependencies = (
    collectionDeps: string[] | undefined,
    currentCollections: string[] | null | undefined
  ): boolean => {
    if (!collectionDeps || collectionDeps.length === 0) {
      return true;
    }

    if (
      !currentCollections ||
      !Array.isArray(currentCollections) ||
      currentCollections.length === 0
    ) {
      return false;
    }

    return collectionDeps.some((dep) =>
      currentCollections.some(
        (collection) => collection === dep || collection.includes(dep)
      )
    );
  };

  const regularDependenciesMet =
    !data?.dependencies ||
    checkDependencies(data.dependencies, section, filtersPayload);
  const collectionDependenciesMet = checkCollectionDependencies(
    data?.collection_dependencies,
    selectedCollectionNames
  );

  if (!(regularDependenciesMet && collectionDependenciesMet)) {
    return null;
  }

  if (
    data?.dependencies &&
    !checkDependencies(data.dependencies, section, filtersPayload)
  ) {
    return null;
  }

  switch (type) {
    case DesignOptions.image_text_list:
      return wrapFilter(
        "imageTextList",
        <ImageTextListSection
          key={`image-text-${section}-${index}`}
          options={options}
          selectionType={data?.selection_type}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          parentFilter={data?.parent_filter}
          selectedStyle={selectedStyle}
          minWidth={data?.min_width}
          selectedProduct={selectedProduct}
          columns={data?.columns}
          enterSecondFlow={mEnterSecondFlow}
          isCollapsible={data?.is_collapsible ?? false}
        />
      );
    case DesignOptions.range_list:
      return wrapFilter(
        "rangeList",
        <RangeListSection
          key={`range-list-${section}-${index}`}
          options={options}
          columns={data?.columns}
          selectionType={data?.selection_type}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionName={section}
          selectedStyle={selectedStyle}
          minLabel={data?.minLabel}
          maxLabel={data?.maxLabel}
          direction={
            fixedDirection ??
            ((theme?.isLargeScreen || theme.isTablet) && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          minWidth={data?.min_width}
          web_width={data?.web_width}
          step={step}
          selectedProduct={selectedProduct}
          enterSecondFlow={mEnterSecondFlow}
          isCollapsible={data?.is_collapsible ?? false}
        />
      );
    case DesignOptions.range_slider:
      return wrapFilter(
        "rangeSlider",
        <RangeSliderSection
          key={`range-slider-${section}-${index}`}
          minVal={data?.min}
          maxVal={data?.max}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          selectedStyle={selectedStyle}
          selectedProduct={selectedProduct}
          isCollapsible={data?.is_collapsible ?? false}
        />
      );
    case DesignOptions.tabs: {
      return wrapFilter(
        "tabs",
        <TabsSection
          key={`tabs-${section}-${index}`}
          sub_filters={data.sub_filters}
          children={data.sub_filters?.map((filters: any, subIndex: number) =>
            renderDesignOptions(
              filters,
              section,
              fromResultScreen,
              selectedStyle,
              subIndex,
              selectedProduct,
              step,
              filterContainerWidth,
              theme,
              t,
              parentDirection,
              fromUnifiedFilters,
              scrollRef,
              mEnterSecondFlow,
              filtersPayload,
              selectedCollectionNames,
              userInfo,
              hideFiltersBasedOnAllowedStockItemsType
            )
          )}
          sectionName={section}
          selectedStyle={selectedStyle}
        />
      );
    }
    case DesignOptions.list:
      return wrapFilter(
        "list",
        <ListSection
          key={`list-${section}-${index}`}
          data={data}
          step={step}
          selectedProduct={selectedProduct}
          direction={
            fixedDirection ??
            ((theme?.isLargeScreen || theme?.isTablet) && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          children={data.sub_filters?.map((filters: any, subIndex: number) =>
            renderDesignOptions(
              filters,
              section,
              fromResultScreen,
              selectedStyle,
              subIndex,
              selectedProduct,
              step,
              filterContainerWidth,
              theme,
              t,
              fixedDirection || data?.direction,
              fromUnifiedFilters,
              scrollRef,
              mEnterSecondFlow,
              filtersPayload,
              selectedCollectionNames,
              userInfo,
              hideFiltersBasedOnAllowedStockItemsType
            )
          )}
          isCollapsible={data?.is_collapsible ?? false}
          sectionName={section}
        />
      );
    case DesignOptions.column:
      return wrapFilter(
        "column",
        <ColumnSection
          key={`column-list-${section}-${index}`}
          data={data}
          selectedProduct={selectedProduct}
          children={data.sub_filters?.map((filters: any, subIndex: number) =>
            renderDesignOptions(
              filters,
              section,
              fromResultScreen,
              selectedStyle,
              subIndex,
              selectedProduct,
              step,
              filterContainerWidth,
              theme,
              t,
              fixedDirection || data?.direction,
              fromUnifiedFilters,
              scrollRef,
              mEnterSecondFlow,
              filtersPayload,
              selectedCollectionNames,
              userInfo,
              hideFiltersBasedOnAllowedStockItemsType
            )
          )}
        />
      );
    case DesignOptions.checkbox_list:
      if (!options) {
        return;
      }
      if (
        mEnterSecondFlow &&
        (data?.collection_dependencies?.length ?? 0) > 0
      ) {
        return null;
      }
      if (data.step && data.step === 1) {
        return wrapFilter(
          "jewelryType",
          <JewelryTypeComponent
            options={options}
            step={step}
            selectionType={data?.selection_type}
            payloadName={data?.payload_name}
            sectionName={section}
            selectedProduct={selectedProduct}
            selectedStyle={selectedStyle}
            scrollRef={scrollRef}
            enterSecondFlow={mEnterSecondFlow}
          />
        );
      }
      return wrapFilter(
        "checkboxList",
        <CheckBoxSection
          key={`checkbox-${section}-${index}`}
          options={options}
          selectionType={data?.selection_type}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          hasSeparator={data?.has_separator}
          sectionName={section}
          selectedStyle={selectedStyle}
          direction={
            fixedDirection ??
            ((theme?.isLargeScreen || theme.isTablet) && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          translation={t}
          subSectionName={data?.name}
          fromUnifiedFilters={fromUnifiedFilters}
          selectedProduct={selectedProduct}
          selectedCollectionNames={selectedCollectionNames}
          isCollapsible={data?.is_collapsible ?? false}
        />
      );
    case DesignOptions.round_checkbox_list:
      if (
        !options ||
        (mEnterSecondFlow &&
          (data?.collection_dependencies?.length ?? 0) > 0) ||
        (hideFiltersBasedOnAllowedStockItemsType &&
          data?.name === "product_type" &&
          userInfo?.hideLabGrown() &&
          userInfo?.hideDiamond())
      ) {
        return;
      }
      return wrapFilter(
        "roundCheckboxList",
        <CheckBoxSection
          key={`round-checkbox-${section}-${index}`}
          options={options}
          selectionType={data?.selection_type}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          isRoundCheckbox={true}
          sectionName={section}
          direction={
            fixedDirection ??
            ((theme?.isLargeScreen || theme?.isTablet) && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          selectedStyle={selectedStyle}
          subSectionName={data?.name}
          translation={t}
          fromUnifiedFilters={fromUnifiedFilters}
          selectedCollectionNames={selectedCollectionNames}
          isCollapsible={data?.is_collapsible ?? false}
          selectedProduct={selectedProduct}
        />
      );
    case DesignOptions.text_list:
      return wrapFilter(
        "textList",
        <TextListSection
          key={`text-list-${section}-${index}`}
          options={options}
          columns={data?.columns}
          selectionType={data?.selection_type}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          selectedStyle={selectedStyle}
          isSubOption={false}
          hasSeparator={data?.has_separator}
          translation={t}
          subSectionName={data?.name}
          minWidth={data?.min_width}
          fromUnifiedFilters={fromUnifiedFilters}
          selectedProduct={selectedProduct}
          isCollapsible={data?.is_collapsible ?? false}
          dependencies={data?.dependencies}
        />
      );
    case DesignOptions.text_list_with_values:
      return wrapFilter(
        "textListWithValues",
        <TextListSectionWithValues
          key={`text-list-values-${section}-${index}`}
          options={options}
          columns={data?.columns}
          direction={
            fixedDirection ??
            (theme?.isLargeScreen && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          minWidth={data?.min_width}
          selectionType={data?.selection_type}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          selectedStyle={selectedStyle}
          selectedProduct={selectedProduct}
          isCollapsible={data?.is_collapsible ?? false}
        />
      );
    case DesignOptions.range:
      return wrapFilter(
        "range",
        <RangeSection
          key={`range-${section}-${index}`}
          data={data}
          sectionName={section}
          selectedStyle={selectedStyle}
          minLabel={data?.minLabel}
          maxLabel={data?.maxLabel}
          subSectionName={data?.name}
          direction={parentDirection}
          step={step}
          parentWidth={filterContainerWidth}
        />
      );
    case DesignOptions.dropdown:
      return wrapFilter(
        "dropdown",
        <SearchFilterSection
          key={`dropdown-${section}-${index}`}
          data={data}
          payloadName={data?.payload_name}
          sectionName={section}
          selectedStyle={selectedStyle}
          sub_filters={data?.sub_filters}
          children={data?.sub_filters?.map((filters, subIndex) =>
            renderDesignOptions(
              filters,
              section,
              fromResultScreen,
              selectedStyle,
              subIndex,
              selectedProduct,
              step,
              filterContainerWidth,
              theme,
              t,
              fixedDirection || data?.direction,
              fromUnifiedFilters,
              scrollRef,
              mEnterSecondFlow,
              filtersPayload,
              selectedCollectionNames,
              userInfo,
              hideFiltersBasedOnAllowedStockItemsType
            )
          )}
        />
      );
    case DesignOptions.image_text_list_range:
      return wrapFilter(
        "imageTextListRange",
        <ImageTextListRangeFilter
          key={`image-text-${section}-${index}`}
          options={options}
          selectionType={data?.selection_type}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          parentFilter={data?.parent_filter}
          selectedStyle={selectedStyle}
          minWidth={data?.min_width}
          selectedProduct={selectedProduct}
          columns={data?.columns}
          parentKey={data?.parent_key}
        />
      );
    case DesignOptions.select_list:
      return wrapFilter(
        "selectList",
        <SelectListItem
          key={`select-list-${section}-${index}`}
          sub_filters={data?.sub_filters}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          parentFilter={data?.parent_filter}
          subSectionName={data?.name}
          selectedStyle={selectedStyle}
          selectedProduct={selectedProduct}
          parentKey={data?.parent_key}
          children={data?.sub_filters?.map((filters: any, subIndex: number) =>
            renderDesignOptions(
              filters,
              section,
              fromResultScreen,
              selectedStyle,
              subIndex,
              selectedProduct,
              step,
              filterContainerWidth,
              theme,
              t,
              fixedDirection || data?.direction,
              fromUnifiedFilters,
              scrollRef,
              filtersPayload,
              selectedCollectionNames,
              userInfo,
              hideFiltersBasedOnAllowedStockItemsType
            )
          )}
          isLastItem={data?.is_last_item ?? false}
        />
      );
    case DesignOptions.collection_dropdown:
      return theme.isLargeScreen
        ? wrapFilter(
            "collectionDropdown",
            <CollectionDropdown
              key={`collection-dropdown-${section}-${index}`}
              data={data}
              sectionName={section}
              selectedProduct={selectedProduct}
              selectedStyle={selectedStyle}
            />
          )
        : null;
    case DesignOptions.collection_subcategories:
      return wrapFilter(
        "collectionSubcategories",
        <CollectionSubcategories
          key={`collection-subcategories-${section}-${
            data?._mergeId || data?.name || index
          }`}
          options={options}
          selectionType={data?.selection_type}
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          hasSeparator={data?.has_separator}
          sectionName={section}
          selectedStyle={selectedStyle}
          direction={
            fixedDirection ??
            (theme?.isLargeScreen && data?.direction_web
              ? data?.direction_web
              : data?.direction)
          }
          translation={t}
          subSectionName={data?.name}
          fromUnifiedFilters={fromUnifiedFilters}
          selectedCollectionNames={selectedCollectionNames}
          isCollapsible={data?.is_collapsible ?? false}
          collectionDependencies={data?.collection_dependencies}
          selectedProduct={selectedProduct}
        />
      );
    case DesignOptions.web_view:
      const showIFrame = true;

      return showIFrame
        ? wrapFilter(
            "webView",
            <IFrameSection
              key={`iframe-${section}-${index}`}
              data={data}
              theme={theme}
              containerWidth={filterContainerWidth}
            />
          )
        : null;
    case DesignOptions.stock_search:
      return wrapFilter(
        "stockSearch",
        <SearchStockSection
          sectionTitle={t(data?.title)}
          productType={selectedProduct}
          payloadName={data?.payload_name}
          sectionName={section}
          placeholder={data?.label ?? ""}
          leftItem={data?.icon_name ? data?.icon_name : ""}
        />
      );
    case DesignOptions.text_list_dynamic_width:
      return wrapFilter(
        "textListWithDynamicWidth",
        <TextListSectionWithDynamicWidth
          key={`text-list-${section}-${index}`}
          options={options}
          columns={data?.columns}
          selectionType={data?.selection_type}
          displayedRows={
            (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
              ? data?.max_row_web
              : data?.max_row || 0
          }
          sectionTitle={t(data?.label)}
          payloadName={data?.payload_name}
          sectionName={section}
          selectedStyle={selectedStyle}
          isSubOption={false}
          hasSeparator={data?.has_separator}
          translation={t}
          subSectionName={data?.name}
          minWidth={data?.min_width}
          fromUnifiedFilters={fromUnifiedFilters}
          selectedProduct={selectedProduct}
          isCollapsible={data?.is_collapsible ?? false}
          dependencies={data?.dependencies}
        />
      );
    default:
      return (
        <VDBText key={`unhandled-${section}-${index}`} isBold>
          Unhandeled
        </VDBText>
      );
  }
};

const renderDesignOptions = (
  data: any,
  section: string,
  fromResultScreen: boolean,
  selectedStyle: SelectedItemTheme,
  index = 0,
  selectedProduct: ProductCategory,
  step: number,
  filterContainerWidth: number,
  theme: DefaultTheme,
  t: any,
  parentDirection: string,
  fromUnifiedFilters: boolean = false,
  scrollRef: any,
  enterSecondFlow: boolean = false,
  filtersPayload?: any,
  selectedCollectionNames: string[] | undefined = [],
  userInfo?: any,
  hideFiltersBasedOnAllowedStockItemsType?: boolean = false
) => {
  if (!data) return null;

  if (typeof data === "object" && !Array.isArray(data)) {
    if (Object.hasOwnProperty.call(data, "options")) {
      return renderItems(
        data,
        section,
        fromResultScreen,
        selectedStyle,
        index,
        selectedProduct,
        step,
        filterContainerWidth,
        theme,
        t,
        parentDirection,
        fromUnifiedFilters,
        scrollRef,
        normalizeBoolean(enterSecondFlow),
        filtersPayload,
        selectedCollectionNames,
        userInfo,
        hideFiltersBasedOnAllowedStockItemsType
      );
    } else if (
      Object.hasOwnProperty.call(data, "sub_filters") ||
      data.type === DesignOptions.list ||
      data.type === DesignOptions.tabs ||
      Object.hasOwnProperty.call(data, "type")
    ) {
      //TODO modify contidion
      return renderItems(
        data,
        section,
        fromResultScreen,
        selectedStyle,
        index,
        selectedProduct,
        step,
        filterContainerWidth,
        theme,
        t,
        parentDirection,
        fromUnifiedFilters,
        scrollRef,
        normalizeBoolean(enterSecondFlow),
        filtersPayload,
        selectedCollectionNames,
        userInfo,
        hideFiltersBasedOnAllowedStockItemsType
      );
    } else if (Object.hasOwnProperty.call(data, "sub_filters")) {
      return data.sub_filters.map((filter: any) =>
        Object.values(filter).map((item, itemIndex) =>
          renderDesignOptions(
            item,
            section,
            fromResultScreen,
            selectedStyle,
            itemIndex,
            selectedProduct,
            step,
            filterContainerWidth,
            theme,
            t,
            parentDirection,
            fromUnifiedFilters,
            scrollRef,
            normalizeBoolean(enterSecondFlow),
            filtersPayload,
            selectedCollectionNames,
            userInfo,
            hideFiltersBasedOnAllowedStockItemsType
          )
        )
      );
    }
  }
};

const FilterSection = ({
  filterData,
  style,
  sectionName,
  selectedProduct,
  filterContainerWidth,
  fromUnifiedFilters,
  scrollRef,
  enterSecondFlow,
}: FilterSectionProps) => {
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const userInfo = useUserInfo();
  const hideFiltersBasedOnAllowedStockItemsType =
    useHideFiltersBasedOnAllowedStockItemsType();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const enableProductSpecificFilterButtonColor =
    useEnableProductSpecificFilterButtonColor();
  const { containerWidth, fromResultScreen } = useContext(FilterScreenContext);
  const collectionDeps = (filterData as any)?.collection_dependencies;
  const payloadDeps = (filterData as any)?.dependencies;
  const needsCollection =
    Array.isArray(collectionDeps) && collectionDeps.length > 0;
  const needsPayload =
    (Array.isArray(payloadDeps) && payloadDeps.length > 0) ||
    "l2_parent_filter" in (filterData as any);

  const filtersPayload = useSelector((state: any) =>
    needsPayload ? state.searchFilters.auxPayload : STABLE_EMPTY_PAYLOAD
  );
  const selectedCollectionNames = useSelector(
    (state: any) =>
      needsCollection
        ? state.searchFilters.collection
        : STABLE_EMPTY_COLLECTIONS,
    needsCollection ? areCollectionsEqualForDeps(collectionDeps) : undefined
  );

  const [isCollapsed, setIsCollapsed] = useState(
    !!(filterData as any)?.is_default_collapsed
  );

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const isFilterCollapsible = !!(filterData as any)?.is_collapsible;
  const filterKey =
    (Array.isArray(filterData?.payload_name)
      ? filterData?.payload_name?.[0]
      : filterData?.payload_name) ||
    filterData?.name ||
    filterData?.type ||
    "filter";

  const filterTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() ?? "section"
  }.${filterKey}`;

  const selectedStyle: SelectedItemTheme = getStyleForSearchCategory(
    selectedProduct ?? "",
    (userInfo?.user?.custom_theme && !enableProductSpecificFilterButtonColor) ??
      false
  );
  const theme = useTheme();
  const normalizedValue =
    fromResultScreen &&
    typeof fromResultScreen === "string" &&
    fromResultScreen === "true"
      ? true
      : fromResultScreen &&
        typeof fromResultScreen === "string" &&
        fromResultScreen === "false"
      ? false
      : fromResultScreen;
  const step = normalizedValue ? 2 : 1;

  const isJewelryProduct = [
    ProductCategory.JEWELRY,
    ProductCategory.LABGROWN_JEWELRY,
  ].includes(selectedProduct as ProductCategory);

  const isJewelryStyleVisible = useCallback(() => {
    if (!isJewelryProduct) return true;
    if (!("l2_parent_filter" in filterData)) return true;
    return filtersPayload?.[sectionName]?.jewelry_styles?.includes(
      (filterData as any)?.payload_name?.[0]
    );
  }, [isJewelryProduct, step, filterData, filtersPayload, sectionName]);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handler = (event: MessageEvent) => {
      const payloads = event?.data;

      if (payloads?.type === "DIAMOND_FILTER_UPDATE") {
        const normalizedPayload = {
          color: payloads?.data?.color,
          shapes: payloads?.data?.shape ? [payloads.data.shape] : [],
          meas_length_from: payloads?.data?.mm?.min ?? 0,
          meas_length_to: payloads?.data?.mm?.max ?? 100,
          size_from: payloads?.data?.carat?.min ?? 0,
          size_to: payloads?.data?.carat?.max ?? 1000,
        };

        DeviceEventEmitter.emit("IFRAME_FILTER_PAYLOAD", normalizedPayload);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [filtersPayload]);

  if (!isJewelryStyleVisible()) return null;

  const contentToRender =
    containerWidth === 0 ? (
      <View style={{ width: "100%" }} />
    ) : (
      renderDesignOptions(
        filterData,
        sectionName ?? "",
        fromResultScreen ?? false,
        selectedStyle,
        0,
        selectedProduct as ProductCategory,
        step,
        theme.isLargeScreen || theme.isTablet
          ? containerWidth ?? 0
          : filterContainerWidth ?? 0,
        theme,
        t,
        null as unknown as string,
        fromUnifiedFilters,
        scrollRef,
        mEnterSecondFlow,
        filtersPayload,
        selectedCollectionNames,
        userInfo,
        hideFiltersBasedOnAllowedStockItemsType
      )
    );

  if (!contentToRender) return null;

  if ((filterData as any)?.type === DesignOptions.collection_dropdown) {
    return (
      <View
        style={{ paddingTop: SpaceType.Extra }}
        testID={`${filterTestId}.collectionDropdown`}
        data-testId={`${filterTestId}.collectionDropdown`}
      >
        {contentToRender}
      </View>
    );
  }

  return (
    <>
      {(filterData as any)?.display_name_outside && !isFilterCollapsible && (
        <TitleSection>
          <VDBText type={typography["H3-M"]} isBold color={theme["core-06"]}>
            {t((filterData as any)?.outside_label) ??
              (filterData as any)?.name ??
              ""}
          </VDBText>
        </TitleSection>
      )}
      {(filterData as any)?.type === DesignOptions.label_only ? (
        (fromResultScreen && theme.isMobile) || !fromResultScreen ? (
          <LabelOnlySection
            data={filterData}
            theme={theme}
            translation={t}
            fromResultScreen={fromResultScreen ?? false}
          />
        ) : (
          <></>
        )
      ) : (
        <ChildContainer
          style={containerWidth === 0 ? {} : style}
          fromResultScreen={fromResultScreen ?? false}
          containerWidth={containerWidth}
          hasPaddingBottom={!isFilterCollapsible || !isCollapsed}
          filterType={(filterData as any)?.type}
          testID={filterTestId}
          data-testId={filterTestId}
        >
          {isFilterCollapsible && (
            <CollapsibleHeader
              title={t(
                (filterData as any)?.label ||
                  (filterData as any)?.outside_label ||
                  (filterData as any)?.name ||
                  ""
              )}
              isCollapsed={isCollapsed}
              onToggle={toggleCollapse}
              selectedStyle={selectedStyle}
              theme={theme}
            />
          )}

          {/* Simple conditional rendering instead of complex animations */}
          {!isFilterCollapsible || !isCollapsed ? contentToRender : null}
        </ChildContainer>
      )}
    </>
  );
};

const ChildContainer = styled.View<{
  fromResultScreen: boolean;
  containerWidth: number;
  hasPaddingBottom: boolean;
  filterType: string;
}>`
  background: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  padding-bottom: ${({
    hasPaddingBottom,
    filterType,
  }: {
    hasPaddingBottom: boolean;
    filterType: string;
  }) =>
    filterType === DesignOptions.web_view ||
    filterType === DesignOptions.checkbox_list ||
    filterType === DesignOptions.collection_subcategories
      ? 0
      : hasPaddingBottom
      ? SpaceType.Extra
      : 0}px;
  border-radius: ${SpaceType.Basic}px;
  ${({ filterType }: { filterType: string }) =>
    filterType === DesignOptions.web_view &&
    css`
      align-items: center;
    `};
  ${({
    theme,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
  }) =>
    !theme.isMobile &&
    !fromResultScreen &&
    css`
      width: 100%;
    `};
  opacity: ${({ containerWidth }: { containerWidth: number }) =>
    containerWidth > 0 ? 1 : 0};
  ${({ fromResultScreen }) =>
    fromResultScreen
      ? css`
          border-width: 1px;
          border-color: ${({ theme }: { theme: DefaultTheme }) =>
            theme["core-03"]};
        `
      : css`
          box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
            getBoxShadow(theme.Shadows.Gentle)};
        `}
`;

const TitleSection = styled.View`
  padding-top: ${SpaceType.Big}px;
`;

const areFilterSectionPropsEqual = (
  prev: FilterSectionProps,
  next: FilterSectionProps
) => {
  const prevData = prev.filterData as any;
  const nextData = next.filterData as any;
  const prevId = prevData?._mergeId || prevData?.name || prevData?.type;
  const nextId = nextData?._mergeId || nextData?.name || nextData?.type;

  return (
    prevId === nextId &&
    (prevData?.options?.length ?? 0) === (nextData?.options?.length ?? 0) &&
    prev.sectionName === next.sectionName &&
    prev.selectedProduct === next.selectedProduct &&
    prev.filterContainerWidth === next.filterContainerWidth &&
    prev.fromUnifiedFilters === next.fromUnifiedFilters &&
    prev.enterSecondFlow === next.enterSecondFlow
  );
};

export default memo(FilterSection, areFilterSectionPropsEqual);
